import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, AlertTriangle, ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const PHRASE = "DELETE TO ERASE EVERYTHING";

export function EraseDataCard() {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const erase = async () => {
    if (confirmText !== PHRASE) { toast.error(`Type "${PHRASE}" to confirm.`); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/erase-all", { confirm: PHRASE });
      setResult(data);
      setConfirmText("");
      toast.success(`Erased everything. Kept admin login: ${(data.admin_usernames || []).join(", ")}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not erase data."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-danger/40" data-testid="settings-erase-data-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-danger/10 text-danger">
            <Trash2 size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-danger">Erase Everything</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Reset the entire system back to zero — customers, subscriptions, payments, deliveries, routes,
              drivers, plans, everything. Your admin username &amp; password are kept so you can still sign in.
            </p>
          </div>
        </div>

        {/* Warning 1 */}
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-bg px-3 py-2.5 text-xs text-danger" data-testid="settings-erase-warning-1">
          <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0" />
          <span><strong>Warning 1:</strong> This permanently deletes ALL data. It cannot be undone from here — make sure you have a backup first.</span>
        </div>
        {/* Warning 2 */}
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-bg px-3 py-2.5 text-xs text-danger" data-testid="settings-erase-warning-2">
          <ShieldAlert size={15} strokeWidth={1.75} className="mt-0.5 shrink-0" />
          <span><strong>Warning 2:</strong> Only your admin login survives. Every customer, driver and all records will be gone immediately.</span>
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-danger">Type <span className="font-mono">{PHRASE}</span> to confirm</label>
          <Input
            value={confirmText}
            disabled={busy}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={PHRASE}
            className="mt-1 max-w-[360px]"
            data-testid="settings-erase-confirm-input"
          />
        </div>

        <Button
          variant="destructive"
          className="mt-3"
          onClick={erase}
          disabled={busy || confirmText !== PHRASE}
          data-testid="settings-erase-button"
        >
          {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Trash2 size={16} strokeWidth={1.75} className="mr-1" />}
          {busy ? "Erasing…" : "Erase everything"}
        </Button>

        {result && (
          <div className="mt-4 rounded-lg border border-success/30 bg-success-bg p-3 text-xs text-success" data-testid="settings-erase-result">
            <p className="flex items-center gap-1.5 font-semibold">
              <CheckCircle2 size={14} /> Done. Cleared {result.cleared_collections} collections. Kept admin login: {(result.admin_usernames || []).join(", ")}.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
